﻿import { LiveDataSet, LiveDataSetValue } from "../../API/LiveData/LiveDataSet";
import { ParameterSchemaStation } from "../../API/Schema/ParameterSchema";
import { IUnitsConversionSet } from "../../Converters/UnitsConverter/IUnitsConverter";
import { DefaultParameterDefinitions } from "../../DefaultParameterDefinitions";
import { DefaultGroupFormatter } from "../../Formatters/DefaultGroupFormatter";
import { ILiveDataListenerContext, ILiveDataListener } from "../../ILiveDataListener";
import { MainSite } from "../../MainSite";
import { ParameterDefinition } from "../../Model/ParameterDefinition";
import { WarningGroup } from "../../Model/WarningGroup";
import { ParameterGroupInfoSet } from "../../ParameterGroupInfoSet";
import { ProfileComponent, Profile } from "../../ValueTypes/Profile";
import { SpeedUnits } from "./Current";
import { DirectionalPolarChart } from "./DirectionalPolarChart";
import { ChartDataPoint, HovmollerChart } from "./HovmollerChart";
import { ISiteObservationsPage } from "./ISiteObservationsPage";
import { MiniChartDataSet, MiniChart } from "./MiniChart";
import { ProfileChart, ProfileTypeEnum } from "./ProfileChart";
import { ProgressiveVectorDiagram } from "./ProgressiveVectorDiagram";
import { ValueBox } from "./ValueBox";

export class RtcpDashboard implements ISiteObservationsPage {
    public id: string = "rtcpdashboard";
    public label: string = "RTCP Buoy";

    public station: ParameterSchemaStation;
    public warningGroup: WarningGroup;
    private _dpc: DirectionalPolarChart;
    private _pvd: ProgressiveVectorDiagram;
    private _chartsHovmoller: HovmollerChart;
    private _chartsHovmollerHorizontal: HovmollerChart;
    private currentProfileSensor?: string = null; //: for Hovmöller chart..
    private currentProfileParam?: string = null; //: for Hovmöller chart..
    private currentProfileHorizontalSensor?: string = null; //: for Hovmöller chart..
    private currentProfileHorizontalParam?: string = null; //: for Hovmöller chart..

    public notifyResize() {}

    public show(station: ParameterSchemaStation): boolean {
        // return station.Station.Region == 'GOM';
        return station.Station.Id == "Australia_Prelude";

        let sensorCount = 0;

        for (let sensorId in station.Sensors) {
            sensorCount++;

            const sensor = station.Sensors[sensorId];

            for (let paramId in sensor.Parameters) {
                if (paramId.indexOf("Current") != -1) {
                    return true;
                }
            }
        }
        return false;
    }

    constructor(
        public mainSite: MainSite,
        private ctx: ILiveDataListenerContext,
    ) {}

    private getStationSchema(): ParameterSchemaStation {
        return this.mainSite.staticDataManager.Schema.ParameterSchema.Stations[
            this.station.Station.Id
        ];
    }

    private stationHasSensor(sensorId: string): boolean {
        return sensorId in this.getStationSchema().Sensors;
    }

    private stationHasParameter(sensorId: string, paramId: string): boolean {
        return (
            sensorId in this.getStationSchema().Sensors &&
            paramId in this.getStationSchema().Sensors[sensorId].Parameters
        );
    }

    private stationGetFirstAvailableParam(
        sensorId: string,
        params: string[],
        searchSlashesReplaced: boolean = true,
        returnFirstRequestedAsDefault: boolean = true,
    ): string {
        //: example on GOM_Vito: Icon.Midi.WebApplication.FrontEnd.MainSite.instance.pages[3].pages[1].stationGetFirstAvailableParam('WM1', ['Hs_m', 'HsT/m', 'Hs/m_30min', 'HsT/m_30min'])
        if (params.length == 0) {
            console.warn(
                "Unable to get first available param as list os params to search provided is empty!",
            );
            return null;
        }

        let out: string = null;

        if (this.stationHasSensor(sensorId)) {
            let paramsAvailable: string[] = Object.keys(
                this.getStationSchema().Sensors[sensorId].Parameters,
            ).sort();
            let paramsAvailableReplaced: string[] = paramsAvailable.map((x) =>
                x.replace(/\//g, "_"),
            );

            for (let i = 0; i <= params.length; i++) {
                let _param: string = params[i];
                if (out == null && paramsAvailable.indexOf(_param) >= 0) {
                    return _param;
                }

                if (
                    out == null &&
                    searchSlashesReplaced &&
                    paramsAvailableReplaced.indexOf(_param) >= 0
                ) {
                    return paramsAvailable[paramsAvailableReplaced.indexOf(_param)];
                }
            }
        }

        if (out == null && returnFirstRequestedAsDefault) {
            return params[0];
        }

        return out;
    }

    /**
     * Get the parameter from schema, having checked for keys with their slashes replaced by underscores (as ParameterGroupInfo usually uses all underscores!).
     * @param sensorId
     * @param paramId
     * @param nullOnNotFound
     */
    private stationGetParameterId(
        sensorId: string,
        paramId: string,
        nullOnNotFound: boolean = false,
    ): string | null {
        //: example on GOM_Vito: Icon.Midi.WebApplication.FrontEnd.MainSite.instance.pages[3].pages[1].stationGetParameterId('AWOS', 'WindDirection_deg')
        //: Object.keys(Icon.Midi.WebApplication.FrontEnd.MainSite.instance.staticDataManager.Schema.ParameterSchema.Stations['GOM_Vito'].Sensors['AWOS'].Parameters).sort().map(x => x.replace(/\//g, '_')).indexOf('WindDirection_deg');
        if (!this.stationHasParameter(sensorId, paramId) && this.stationHasSensor(sensorId)) {
            let params: string[] = Object.keys(
                this.getStationSchema().Sensors[sensorId].Parameters,
            ).sort();
            let paramsReplaced: string[] = params.map((x) => x.replace(/\//g, "_"));
            if (paramsReplaced.indexOf(paramId) >= 0) {
                return params[paramsReplaced.indexOf(paramId)];
            }
        }

        if (!this.stationHasParameter(sensorId, paramId) && nullOnNotFound) {
            return null;
        }

        return paramId;
    }

    private getSensorLowestBinParam(sensorId: string, paramIdPrefix: string): string {
        if (this.stationHasSensor(sensorId)) {
            let params: string[] = Object.keys(
                this.getStationSchema().Sensors[sensorId].Parameters,
            )
                .sort()
                .filter((x) => x.substring(0, paramIdPrefix.length) == paramIdPrefix);
            // console.info('getSensorLowestBinParam', sensorId, paramIdPrefix, params);
            // Object.keys(Icon.Midi.WebApplication.FrontEnd.MainSite.instance.staticDataManager.Schema.ParameterSchema.Stations['GOM_Vito'].Sensors['LiDAR'].Parameters).sort().filter(x => x.substring(0, 'WindDirection_Bin'.length) == 'WindDirection_Bin');

            if (params.length >= 1) {
                return params[0];
            }
        }

        return null;
    }

    private getParamDefs(sensorId: string, paramId: string): ParameterDefinition | null {
        let defs: ParameterDefinition = null;

        if (this.stationHasParameter(sensorId, paramId)) {
            defs =
                MainSite.instance.staticDataManager.Schema.ParameterSchema.Stations[
                    this.station.Station.Id
                ].Sensors[sensorId].Parameters[paramId].Variants[0].ParameterDefinition;
        }

        if ((!defs || defs.Default) && paramId in DefaultParameterDefinitions.Parameters) {
            defs = DefaultParameterDefinitions.Parameters[paramId];
        }

        return defs;
    }

    public render(
        container: JQuery,
        listeners: ILiveDataListener[],
        station: ParameterSchemaStation,
        warningGroup: WarningGroup,
    ): void {
        this.station = station;
        this.warningGroup = warningGroup;

        /*
         * Quick and dirty fix to detect if current speed is shown as kts rather than mps.
         * It would be much better to allow the DefaultGroupFormatter to choose the group from
         * multiple options based on what is available.
         */
        let currentSpeedUnits = SpeedUnits.mps;
        let hasHorizontal = false;
        for (const sensorId in station.Sensors) {
            const sensor = station.Sensors[sensorId];
            /*if (Object.keys(sensor.Parameters).some(pid => pid == "CurrentProfile") && !this.currentProfileParam) {
                this.currentProfileSensor = sensorId;
                this.currentProfileParam = "CurrentProfile";
                // break;
            }*/
            if (Object.keys(sensor.Parameters).some((pid) => pid == "CurrentProfile/kts")) {
                currentSpeedUnits = SpeedUnits.kts;
                /*if (!this.currentProfileParam) {
                    this.currentProfileSensor = sensorId;
                    this.currentProfileParam = "CurrentProfile/kts";
                }*/
                // break;
            }
            if (
                Object.keys(sensor.Parameters).some(
                    (pid) => pid.indexOf("HorizontalCurrentProfile") === 0,
                )
            ) {
                hasHorizontal = true;
                /*if (!this.currentProfileHorizontalParam) {
                    this.currentProfileHorizontalSensor = sensorId;
                    this.currentProfileHorizontalParam = "HorizontalCurrentProfile";
                }*/
                // break;
            }
        }

        //: get unitConversionSet's and factors (ucf_ prefixed vars = "unit conversion factor"):
        var speedUnitsStr = currentSpeedUnits == SpeedUnits.mps ? "m/s" : "kts";
        var unitsStrDepthOrHeight = "m";
        let unitsConversionSet_DepthOrHeight: IUnitsConversionSet | null =
            MainSite.getUnitConverter().getConversionSet("m");
        let unitsConversionSet_Speed: IUnitsConversionSet | null =
            MainSite.getUnitConverter().getConversionSet(speedUnitsStr);
        let ucf_DepthOrHeight: (value: any) => any = (value: any) => value;
        let ucf_Speed: (value: any) => any = (value: any) => value;
        if (unitsConversionSet_DepthOrHeight) {
            ucf_DepthOrHeight = unitsConversionSet_DepthOrHeight.convertValue;
            unitsStrDepthOrHeight = unitsConversionSet_DepthOrHeight.convertedUnits;
        }
        if (unitsConversionSet_Speed) {
            ucf_Speed = unitsConversionSet_Speed.convertValue;
            speedUnitsStr = unitsConversionSet_Speed.convertedUnits;
        }

        var page = $('<div class="midi_SiteObservations_padded_page_content"></div>');

        let panelsHeightEm: number = 47;
        var leftPanel = $("<div></div>")
            .css("position", "absolute")
            .css("height", `${panelsHeightEm}em`)
            .css("width", "12em")
            .appendTo(page);

        var centrePanel = $("<div></div>")
            .css("position", "absolute")
            .css("height", `${panelsHeightEm}em`)
            .css("left", "13em")
            .css("width", "25em") // 12 + 1 + 12
            .appendTo(page);

        var rightPanel = $("<div></div>")
            .css("position", "absolute")
            .css("height", `${panelsHeightEm}em`)
            .css("left", "39em")
            .css("width", "25em")
            .appendTo(page);

        //: common value box settings:
        let vbHeight: number = 13;
        let vbSepVertical: number = 1;
        let vbFontSizeEm: number = 1.8;
        let vbFontSizeTitle: number | null = 0.75;

        let colCurrentTop: number = 0;

        //: left panel:
        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(ParameterGroupInfoSet.AirTemperature_degC, "RTCP/Met"),
                colCurrentTop,
                0,
                vbHeight,
                100,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        colCurrentTop += vbHeight + vbSepVertical;

        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(ParameterGroupInfoSet.WindSpeed_kts, "RTCP/Met"),
                colCurrentTop,
                0,
                vbHeight,
                100,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        colCurrentTop += vbHeight + vbSepVertical;

        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(ParameterGroupInfoSet.WindDirection_deg, "RTCP/Met"),
                colCurrentTop,
                0,
                vbHeight,
                100,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        colCurrentTop += vbHeight + vbSepVertical;

        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(ParameterGroupInfoSet.GustSpeed_kts, "RTCP/Met"),
                colCurrentTop,
                0,
                vbHeight,
                100,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        colCurrentTop += vbHeight + vbSepVertical;

        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(ParameterGroupInfoSet.Hs_m, "RTCP/Waves"),
                colCurrentTop,
                0,
                vbHeight,
                100,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        colCurrentTop += vbHeight + vbSepVertical;

        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(ParameterGroupInfoSet.Hmax_m, "RTCP/Waves"),
                colCurrentTop,
                0,
                vbHeight,
                100,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        colCurrentTop += vbHeight + vbSepVertical;

        listeners.push(
            new ValueBox(
                this,
                leftPanel,
                new DefaultGroupFormatter(ParameterGroupInfoSet.Tp_s, "RTCP/Waves"),
                colCurrentTop,
                0,
                vbHeight,
                100,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        //: center panel:
        colCurrentTop = 0;

        listeners.push(
            new ValueBox(
                this,
                centrePanel,
                new DefaultGroupFormatter(ParameterGroupInfoSet.RelativeHumidity_pc, "RTCP/Met"),
                colCurrentTop,
                0,
                vbHeight,
                49,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        listeners.push(
            new ValueBox(
                this,
                centrePanel,
                new DefaultGroupFormatter(ParameterGroupInfoSet.QNH_mbar, "RTCP/Met"),
                colCurrentTop,
                51,
                vbHeight,
                49,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        colCurrentTop += vbHeight + vbSepVertical;

        //: determine which sensors/params to use:
        let getPairFromSchema = (
            sensorId: string,
            parameterId: string,
            ...params: string[]
        ): { sensor: string; param: string } => {
            for (let i = 0; i + 1 < params.length; i += 2) {
                const defaultSensorId = params[i];
                const defaultParamId = params[i + 1];
                /// this.mainSite.staticDataManager.Schema.ParameterSchema.Stations[this.station.Station.Id].Sensors
                if (
                    !this.stationHasParameter(sensorId, parameterId) &&
                    defaultSensorId &&
                    defaultParamId &&
                    this.stationHasParameter(defaultSensorId, defaultParamId)
                ) {
                    return {
                        sensor: defaultSensorId,
                        param: defaultParamId,
                    };
                }
            }

            //: always return something (so here the main element requested even if not necessarily available, seen as the default is nto available):
            return {
                sensor: sensorId,
                param: parameterId,
            };
        };

        let miniChartsSensors: { [key: string]: { sensor: string; param: string } } = {
            windSpd: {
                sensor: "RTCP/Met",
                param: "WindSpd1",
            },
            windDir: {
                sensor: "RTCP/Met",
                param: "WindDirn1",
            },
            gustSpd: {
                sensor: "RTCP/Met",
                param: "GustSpd1",
            },

            waveHs: {
                sensor: "RTCP/Waves",
                param: "Hs_m",
            },
            waveHmax: {
                sensor: "RTCP/Waves",
                param: "HmaxT_m",
            },
            waveTp: {
                sensor: "RTCP/Waves",
                param: "Tp_s",
            },
        };

        // console.info('ShellSiteObs Page - miniChartsSensors:', miniChartsSensors);

        let $containerChartWind = $("<div>")
            .css({
                position: "absolute",
                top: `${colCurrentTop}%`,
                left: 0,
                height: `${3 * vbHeight + 2 * vbSepVertical}%`,
                width: "100%",
            })
            .appendTo(centrePanel);
        $containerChartWind.append('<em class="text-muted">Wind chart loading...</em>');
        $containerChartWind.css({ fontSize: "0.85em" }); //: , border: '1px dashed orange', backgroundColor: 'rgba(0,0,255,.1)'

        let minichartDataWind: MiniChartDataSet[] = [
            //: TODO: use appropriate sensor based on available data in ParameterSchema..
            {
                stationId: this.station.Station.Id,
                sensorId: miniChartsSensors.windSpd.sensor,
                parameterId: miniChartsSensors.windSpd.param,
                // parameterDefs: this.getParamDefs(miniChartsSensors.windSpd.sensor, miniChartsSensors.windSpd.param),
                units: "kts",
                plotLine: true,
                yAxisIndex: 0,
            },
            {
                stationId: this.station.Station.Id,
                sensorId: miniChartsSensors.windDir.sensor,
                parameterId: miniChartsSensors.windDir.param,
                // parameterDefs: this.getParamDefs(miniChartsSensors.windDir.sensor, miniChartsSensors.windDir.param),
                units: "deg",
                plotLine: false,
                yAxisIndex: 1,
            },
            {
                stationId: this.station.Station.Id,
                sensorId: miniChartsSensors.gustSpd.sensor,
                parameterId: miniChartsSensors.gustSpd.param,
                // parameterDefs: this.getParamDefs(miniChartsSensors.gustSpd.sensor, miniChartsSensors.gustSpd.param),
                units: "kts",
                plotLine: true,
                yAxisIndex: 0,
            },
        ];
        let yAxisOptionsWind: __Highcharts.AxisOptions[] = [
            {
                title: {
                    text: "Speed (kts)",
                },
                opposite: false,
            },
            {
                title: {
                    text: "Direction (°T)",
                },
                min: 0,
                max: 360,
                opposite: true,
            },
        ];
        let minichartWind = new MiniChart(
            this.mainSite,
            this,
            "Wind Chart",
            $containerChartWind,
            minichartDataWind,
            yAxisOptionsWind,
        );
        listeners.push(minichartWind);

        colCurrentTop += 3 * vbHeight + 2 * vbSepVertical + vbSepVertical;

        let $containerChartWave = $("<div>")
            .css({
                position: "absolute",
                top: `${colCurrentTop}%`,
                left: 0,
                height: `${3 * vbHeight + 2 * vbSepVertical}%`,
                width: "100%",
            })
            .appendTo(centrePanel);
        $containerChartWave.append('<em class="text-muted">Wave chart loading...</em>');
        $containerChartWave.css({ fontSize: "0.85em" }); //: , border: '1px dashed orange', backgroundColor: 'rgba(0,0,255,.1)'

        let minichartDataWave: MiniChartDataSet[] = [
            //: TODO: use appropriate sensor based on available data in ParameterSchema..
            {
                stationId: this.station.Station.Id,
                sensorId: miniChartsSensors.waveHs.sensor,
                parameterId: miniChartsSensors.waveHs.param,
                // parameterDefs: this.getParamDefs(miniChartsSensors.waveHs.sensor, miniChartsSensors.waveHs.param),
                units: "m",
                plotLine: true,
                yAxisIndex: 0,
            },
            {
                stationId: this.station.Station.Id,
                sensorId: miniChartsSensors.waveHmax.sensor,
                parameterId: miniChartsSensors.waveHmax.param,
                // parameterDefs: this.getParamDefs(miniChartsSensors.waveHmax.sensor, miniChartsSensors.waveHmax.param),
                units: "m",
                plotLine: true,
                yAxisIndex: 0,
            },
            {
                stationId: this.station.Station.Id,
                sensorId: miniChartsSensors.waveTp.sensor,
                parameterId: miniChartsSensors.waveTp.param,
                // parameterDefs: this.getParamDefs(miniChartsSensors.waveTp.sensor, miniChartsSensors.waveTp.param),
                units: "s",
                plotLine: false,
                yAxisIndex: 1,
            },
        ];
        let yAxisOptionsWave: __Highcharts.AxisOptions[] = [
            {
                title: {
                    text: `Wave Height (${unitsStrDepthOrHeight})`,
                },
            },
            {
                title: {
                    text: "Wave Period (s)",
                },
                opposite: true,
            },
        ];
        let minichartWave = new MiniChart(
            this.mainSite,
            this,
            "Wave Chart",
            $containerChartWave,
            minichartDataWave,
            yAxisOptionsWave,
        );
        listeners.push(minichartWave);

        //: right panel:
        colCurrentTop = 0;

        listeners.push(
            new ValueBox(
                this,
                rightPanel,
                new DefaultGroupFormatter(
                    ParameterGroupInfoSet.Total_CurrentDirection_Surface_deg_Mean,
                    "RTCP/Currents",
                ),
                colCurrentTop,
                0,
                vbHeight,
                49,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        listeners.push(
            new ValueBox(
                this,
                rightPanel,
                new DefaultGroupFormatter(
                    ParameterGroupInfoSet.Total_CurrentSpeed_Surface_mps_Mean,
                    "RTCP/Currents",
                ),
                colCurrentTop,
                51,
                vbHeight,
                49,
                this.ctx,
                true,
                vbFontSizeEm,
                vbFontSizeTitle,
            ),
        );

        colCurrentTop += vbHeight + vbSepVertical;

        let $containerChartProfiles = $("<div>")
            .css({
                position: "absolute",
                top: `${colCurrentTop}%`,
                left: 0,
                height: `${100 - colCurrentTop}%`,
                width: "100%",
            })
            .appendTo(rightPanel);
        // $containerChartProfiles.css({ border: '1px dashed orange', backgroundColor: 'rgba(0,0,255,.1)' });

        let chartSelectTabs = $('<div class="current_chart_selector"></div>').appendTo(
            $containerChartProfiles,
        );
        chartSelectTabs.css({ fontSize: "0.85em" });

        let currentBox = $(
            '<div class="site-obs-current-charts" data-chart-type="vertical"></div>',
        )
            .appendTo($containerChartProfiles)
            .hide();
        let currentBoxHorizontal = $(
            '<div class="site-obs-current-charts" data-chart-type="horizontal"></div>',
        )
            .appendTo($containerChartProfiles)
            .hide();
        let pvdBox = $('<div class="site-obs-current-charts" data-chart-type="pvd"></div>')
            .appendTo($containerChartProfiles)
            .hide();
        let polarBox = $('<div class="site-obs-current-charts" data-chart-type="polar"></div>')
            .appendTo($containerChartProfiles)
            .hide();
        let hovmollerBox = $(
            '<div class="site-obs-current-charts" data-chart-type="hovmoller"><em class="text-muted">Loading Hovmöller chart...</em></div>',
        )
            .appendTo($containerChartProfiles)
            .hide();
        let hovmollerBoxHorizontal = $(
            '<div class="site-obs-current-charts" data-chart-type="hovmoller-horizontal"><em class="text-muted">Loading Hovmöller chart...</em></div>',
        )
            .appendTo($containerChartProfiles)
            .hide();

        let showChart = (chartType: string, label: string) => {
            $containerChartProfiles.find(".site-obs-current-charts").hide();
            $containerChartProfiles
                .find(`.site-obs-current-charts[data-chart-type="${chartType}"]`)
                .show();
            $containerChartProfiles.find(".site-obs-current-chart-select-label").text(label);
            MainSite.instance.dispatchResize();
        };

        chartSelectTabs.append("<span>Chart: </span>");
        let $buttonGroup: JQuery = $('<div class="btn-group btn-group-sm"></div>').appendTo(
            chartSelectTabs,
        );
        $(
            '<button type="button" class="btn btn-default dropdown-toggle" data-toggle="dropdown"><span class="site-obs-current-chart-select-label">Select chart..</span> <span class="caret"></span></button>',
        ).appendTo($buttonGroup);
        let tpul = $("<ul></ul>")
            .addClass("dropdown-menu")
            // .append($('<span></span>').text("Chart: "))
            .appendTo($buttonGroup);

        this._dpc = new DirectionalPolarChart(
            this.mainSite,
            this,
            polarBox,
            5,
            0,
            93,
            100,
            currentSpeedUnits == SpeedUnits.mps
                ? ParameterGroupInfoSet.Total_CurrentSpeed_Surface_mps_Mean.Id
                : ParameterGroupInfoSet.Total_CurrentSpeed_Surface_kts_Mean.Id,
            ParameterGroupInfoSet.Total_CurrentDirection_Surface_deg_Mean.Id,
            true,
        );

        listeners.push(this._dpc);

        this._pvd = new ProgressiveVectorDiagram(
            this.mainSite,
            this,
            pvdBox,
            5,
            0,
            93,
            100,
            currentSpeedUnits,
            "RTCP/Currents",
        );

        listeners.push(this._pvd);

        listeners.push(
            new ProfileChart(
                this,
                currentBox,
                5,
                0,
                93,
                100,
                currentSpeedUnits,
                "Current",
                ProfileTypeEnum.Depth,
                "RTCP/Met",
            ),
        );

        listeners.push(
            new ProfileChart(
                this,
                currentBoxHorizontal,
                5,
                0,
                93,
                100,
                currentSpeedUnits,
                "Current",
                ProfileTypeEnum.Horizontal,
                "RTCP/Met",
            ),
        );

        let hovmolOptions: __Highcharts.Options = HovmollerChart.chartOptionsDefault();
        hovmolOptions.tooltip = {
            headerFormat: `Speed (${speedUnitsStr})<br/>`,
            pointFormat: `{point.x:%d/%m/%Y %H:%M} (<em>{point.y:.1f}${unitsStrDepthOrHeight}</em>): <b>{point.value:.1f}</b>`, //: https://api.highcharts.com/class-reference/Highcharts.Time#dateFormat
        };
        hovmolOptions.yAxis = {
            ...hovmolOptions.yAxis,
            title: {
                text: `Depth (${unitsStrDepthOrHeight})`,
            },
            reversed: true,
        };

        // Speed: Options specific to speed chart
        let hovmolOptionsSpeed: __Highcharts.Options = HovmollerChart.chartOptionsDefault();
        hovmolOptionsSpeed.tooltip = {
            headerFormat: `Speed (${speedUnitsStr})<br/>`,
            pointFormat: `{point.x:%d/%m/%Y %H:%M} (<em>{point.y:.1f}${unitsStrDepthOrHeight}</em>): <b>{point.value:.1f}</b>`, //: https://api.highcharts.com/class-reference/Highcharts.Time#dateFormat
        };
        hovmolOptionsSpeed.yAxis = {
            ...hovmolOptionsSpeed.yAxis,
            title: {
                text: `Depth (${unitsStrDepthOrHeight})`,
            },
            reversed: true,
        };
        hovmolOptionsSpeed.colorAxis = {
            ...hovmolOptionsSpeed.colorAxis,
            stops: [
                // Jet colorscheme
                [0, "#0000FF"], // Blue
                [0.33, "#00FFFF"], // Cyan
                [0.66, "#FFFF00"], // Yellow
                [1, "#FF0000"], // Red
            ],
        };

        // Direction: Options specific to direction chart
        let hovmolOptionsDirection: __Highcharts.Options = HovmollerChart.chartOptionsDefault();
        hovmolOptionsDirection.tooltip = {
            headerFormat: `Direction (deg)<br/>`,
            pointFormat: `{point.x:%d/%m/%Y %H:%M} (<em>{point.y:.1f}${unitsStrDepthOrHeight}</em>): <b>{point.value:.1f}</b>`, //: https://api.highcharts.com/class-reference/Highcharts.Time#dateFormat
        };
        hovmolOptionsDirection.yAxis = {
            ...hovmolOptionsSpeed.yAxis,
            title: {
                text: `Depth (${unitsStrDepthOrHeight})`,
            },
            reversed: true,
        };
        hovmolOptionsDirection.colorAxis = {
            ...hovmolOptionsDirection.colorAxis,
            stops: [
                [0.0, "rgb(255, 0, 0)"],
                [0.25, "rgb(160, 32, 240)"],
                [0.5, "rgb(0, 0, 255)"],
                [0.75, "rgb(252, 186, 3)"],
                [1.0, "rgb(255, 0, 0)"],
            ],
            //tickInterval: 180,
            tickPositions: [0, 90, 180, 270, 360],
            startOnTick: true,
            endOnTick: true,
        };

        this._chartsHovmoller = new HovmollerChart(
            this.mainSite,
            this,
            hovmollerBox,
            0,
            0,
            93,
            100,
            //// specific chart options
            hovmolOptionsSpeed,
            hovmolOptionsDirection,
            //// getDataMethod
            (
                tsFrom: moment.Moment,
                tsTo: moment.Moment,
                isSpeed: boolean,
                extractProfileCmpValue: (cp: ProfileComponent) => number,
                chart: HovmollerChart,
                callback: (
                    data: ChartDataPoint[],
                    isFailed: boolean,
                    failedMessage?: string,
                ) => void,
            ): void => {
                let res: ChartDataPoint[] = [];

                if (this.currentProfileSensor == null || this.currentProfileParam == null) {
                    console.warn(
                        "Current Hovmoller (Vertical) chart: no current profile sensor or param detected for this station.",
                    );
                    callback([], false);
                    return;
                }

                let sensor = this.currentProfileSensor;
                let parameter = this.currentProfileParam;
                let uri = `/api/v1/HistoricalData/P/${this.station.Station.Id}/${encodeURIComponent(sensor)}/${encodeURIComponent(parameter)}?fromUnixMillis=${tsFrom.valueOf()}&toUnixMillis=${tsTo.valueOf()}`;
                console.info("Current Hovmoller (Vertical) - data fetch URI:", uri);

                $.ajax(uri + "", {
                    cache: false,
                    dataType: "json",
                    type: "GET",
                    timeout: 30000,
                    success: (data: any, text: string, jqXHR: JQueryXHR) => {
                        try {
                            if (Array.isArray(data.results[0].series[0].values)) {
                                for (let i = 0; i < data.results[0].series[0].values.length; i++) {
                                    let x = data.results[0].series[0].values[i];
                                    let m: moment.Moment = moment.utc(
                                        x[0],
                                        "YYYY-MM-DD HH:mm:ssZ",
                                        true,
                                    );
                                    if (m && m.isValid()) {
                                        let profile = JSON.parse(x[1]) as Profile;

                                        let components = profile.Components.sort(
                                            (a, b) => b.Depth - a.Depth,
                                        ); //: TODO: check sorting!!
                                        components.forEach((cp, cpi) => {
                                            if (!cp.Speed || isNaN(cp.Speed)) {
                                                return;
                                            }

                                            res.push({
                                                timestamp: m.valueOf(),
                                                y: ucf_DepthOrHeight(cp.Depth),
                                                val: isSpeed ? ucf_Speed(cp.Speed) : cp.Direction,
                                            });
                                        });
                                    } else {
                                        console.warn(
                                            "Unable to parse " +
                                                this.currentProfileParam +
                                                " Profile Data from server response. Error: Timestamp not parseable to a valid Moment.",
                                        );
                                        // this.drawChartMessage("Error while retrieving " + this.profileName + " Profile Data.");
                                    }
                                }

                                callback(res, false);
                            }
                        } catch (err) {
                            console.warn(
                                "Unable to parse " +
                                    this.currentProfileParam +
                                    " Profile Data from server response. Error: ",
                                err,
                            );
                            callback([], true);
                        }
                    },
                    error: (jqXHR: JQueryXHR, textStatus: string, errorThrown: string) => {
                        console.warn(
                            "Unable to parse " +
                                this.currentProfileParam +
                                " Profile Data from server response. Error: ",
                            errorThrown,
                        );
                        callback([], true);
                    },
                });
            },
            // to help data fetching methods use livedata if necessary (e.g. to determine sensors/params IDs..)
            (latestData: LiveDataSet, chart: HovmollerChart): void => {
                if (this.currentProfileParam !== null) {
                    chart.updateChart();
                    return; //: already dealt with.
                }

                let liveDataSetValue: LiveDataSetValue = null;
                if (latestData.Stations[this.station.Station.Id]) {
                    if (
                        Object.keys(latestData.Stations[this.station.Station.Id].GroupValues).some(
                            (pid) => pid == "CurrentProfile",
                        )
                    ) {
                        liveDataSetValue =
                            latestData.Stations[this.station.Station.Id].GroupValues[
                                "CurrentProfile"
                            ][0];
                        this.currentProfileSensor = liveDataSetValue.Sensor;
                        this.currentProfileParam = "CurrentProfile";
                    }
                }

                chart.updateChart();
            },
        );
        listeners.push(this._chartsHovmoller);

        if (hasHorizontal) {
            let hovmolOptionsSpeedHorizontal: __Highcharts.Options = {
                ...hovmolOptionsSpeed,
            };
            hovmolOptionsSpeedHorizontal.yAxis = {
                ...hovmolOptionsSpeedHorizontal.yAxis,
                title: {
                    text: `Distance (${unitsStrDepthOrHeight})`,
                },
                reversed: true,
            };

            this._chartsHovmollerHorizontal = new HovmollerChart(
                this.mainSite,
                this,
                hovmollerBoxHorizontal,
                0,
                0,
                93,
                100,
                //// specific chart options
                hovmolOptionsSpeedHorizontal,
                hovmolOptionsDirection,
                //// getDataMethod
                (
                    tsFrom: moment.Moment,
                    tsTo: moment.Moment,
                    isSpeed: boolean,
                    extractProfileCmpValue: (cp: ProfileComponent) => number,
                    chart: HovmollerChart,
                    callback: (
                        data: ChartDataPoint[],
                        isFailed: boolean,
                        failedMessage?: string,
                    ) => void,
                ): void => {
                    let res: ChartDataPoint[] = [];

                    /*if (!this.mainSite.liveDataManager.latestData) {
                        console.warn('Current Hovmoller chart: waiting for live data..');
                        callback([], true);
                        return;
                    }*/

                    if (chart.latestData == null) {
                        console.warn(
                            "Current Hovmoller (Horizontal) chart: waiting for first live data.",
                        );
                        callback([], false, "Awaiting first Live Data...");
                        return;
                    }

                    if (
                        this.currentProfileHorizontalSensor == null ||
                        this.currentProfileHorizontalParam == null
                    ) {
                        console.warn(
                            "Current Hovmoller (Horizontal) chart: no current profile sensor or param detected for this station.",
                        );
                        callback([], false);
                        return;
                    }

                    let sensor = this.currentProfileHorizontalSensor;
                    let parameter = this.currentProfileHorizontalParam;
                    let uri = `/api/v1/HistoricalData/P/${this.station.Station.Id}/${encodeURIComponent(sensor)}/${encodeURIComponent(parameter)}?fromUnixMillis=${tsFrom.valueOf()}&toUnixMillis=${tsTo.valueOf()}`;
                    console.info("Current Hovmoller (Horizontal) - data fetch URI:", uri);

                    $.ajax(uri + "", {
                        cache: false,
                        dataType: "json",
                        type: "GET",
                        timeout: 30000,
                        success: (data: any, text: string, jqXHR: JQueryXHR) => {
                            try {
                                if (Array.isArray(data.results[0].series[0].values)) {
                                    for (
                                        let i = 0;
                                        i < data.results[0].series[0].values.length;
                                        i++
                                    ) {
                                        let x = data.results[0].series[0].values[i];
                                        let m: moment.Moment = moment.utc(
                                            x[0],
                                            "YYYY-MM-DD HH:mm:ssZ",
                                            true,
                                        );
                                        if (m && m.isValid()) {
                                            let profile = JSON.parse(x[1]) as Profile;

                                            let components = profile.Components.sort(
                                                (a, b) =>
                                                    b.Distance ?? b.Depth - a.Distance ?? a.Depth,
                                            );
                                            components.forEach((cp, cpi) => {
                                                if (!cp.Speed || isNaN(cp.Speed)) {
                                                    return;
                                                }

                                                res.push({
                                                    timestamp: m.valueOf(),
                                                    y: ucf_DepthOrHeight(cp.Distance ?? cp.Depth),
                                                    val: ucf_Speed(cp.Speed),
                                                });
                                            });
                                        } else {
                                            console.warn(
                                                "Unable to parse " +
                                                    this.currentProfileHorizontalParam +
                                                    " Profile Data from server response. Error: Timestamp not parseable to a valid Moment.",
                                            );
                                            // this.drawChartMessage("Error while retrieving " + this.profileName + " Profile Data.");
                                        }
                                    }

                                    callback(res, false);
                                }
                            } catch (err) {
                                console.warn(
                                    "Unable to parse " +
                                        this.currentProfileHorizontalParam +
                                        " Profile Data from server response. Error: ",
                                    err,
                                );
                                callback([], true);
                            }
                        },
                        error: (jqXHR: JQueryXHR, textStatus: string, errorThrown: string) => {
                            console.warn(
                                "Unable to parse " +
                                    this.currentProfileHorizontalParam +
                                    " Profile Data from server response. Error: ",
                                errorThrown,
                            );
                            callback([], true);
                        },
                    });
                },
                // to help data fetching methods use livedata if necessary (e.g. to determine sensors/params IDs..)
                (latestData: LiveDataSet, chart: HovmollerChart): void => {
                    if (this.currentProfileHorizontalParam !== null) {
                        return; //: already dealt with.
                    }

                    let liveDataSetValue: LiveDataSetValue = null;
                    if (latestData.Stations[this.station.Station.Id]) {
                        if (
                            Object.keys(
                                latestData.Stations[this.station.Station.Id].GroupValues,
                            ).some((pid) => pid == "HorizontalCurrentProfile")
                        ) {
                            liveDataSetValue =
                                latestData.Stations[this.station.Station.Id].GroupValues[
                                    "HorizontalCurrentProfile"
                                ][0];
                            this.currentProfileHorizontalSensor = liveDataSetValue.Sensor;
                            this.currentProfileHorizontalParam = "HorizontalCurrentProfile";
                        }
                    }

                    chart.updateChart();
                },
            );
            listeners.push(this._chartsHovmollerHorizontal);
        }

        let tpulLiActiveClass = "active"; //: 'selected' prior to using drop-down
        let charts = [
            { key: "vertical", label: "Vertical Profile" },
            // { key: 'horizontal', label: 'Horizontal Profile' },
            { key: "pvd", label: "PVD" },
            { key: "polar", label: "Polar" },
            { key: "hovmoller", label: hasHorizontal ? "Hovmöller (Vertical)" : "Hovmöller" },
            // { key: 'hovmoller-horizontal', label: 'Hovmöller (Horizontal)' },
        ];
        if (hasHorizontal) {
            charts = [
                { key: "vertical", label: "Vertical Profile" },
                { key: "horizontal", label: "Horizontal Profile" },
                { key: "pvd", label: "PVD" },
                { key: "polar", label: "Polar" },
                { key: "hovmoller", label: hasHorizontal ? "Hovmöller (Vertical)" : "Hovmöller" },
                { key: "hovmoller-horizontal", label: "Hovmöller (Horizontal)" },
            ];
        }

        for (var i = 0; i < charts.length; i++) {
            let chart = charts[i];
            let $li = $(`<li class="dropdown-menu-item"></li>`);
            let $a = $(`<a href="">${chart.label}</a>`).appendTo($li);
            $a.on("click", (ev) => {
                ev.preventDefault();
                tpul.children("li").removeClass(tpulLiActiveClass);
                $li.addClass(tpulLiActiveClass);
                showChart(chart.key, chart.label);
            });
            tpul.append($li);
        }

        // currentBox.show();
        setTimeout(() => {
            //: somehow, if executed immediately, this would not display the correct box..
            tpul.children("li").first().find("a").trigger("click");
        }, 500);

        page.appendTo(container);
        //page resizes due to highcharts charts being drawn before the box size is fixed and
        //the chart subsequently not filling the available space
        MainSite.instance.dispatchResize();
    }
}
